import {
  VisualizationDataTableFilterControlType as ControlType,
  VisualizationDataTableFilterLayoutType as LayoutType,
  VisualizationDataTableFilterType
} from "~/enums/visualizations/visualizationDataTableFilterType";
import VisualizationDataTableFilter from "~/models/visualizations/visualizationDataTableFilter";
import {DateFilterMode} from "~/models/visualizations/dateFilterMode";
import {DurationUnit} from "~/enums/durationUnit";
import TrackedArrayProxy from "../../../formability/form_builder/models/trackedArrayProxy";
import {SearchMatchType} from "~/enums/searchMatchType";
import {VerticalAlignment} from "../../enums/verticalAlignment";

export enum VisualizationDataTableFilterCapability {
  containerable = 1,
  labelable = 2,
  defaultable = 3,
  conditionable = 4,
  selectable = 5,
  dateModable = 6,
  dataPointReferencable = 7,
  multiDataPointReferencable = 8,
  placeholderable = 9,
  searchTypable = 10,
  helpTextable = 11,
  columnLayoutable = 12,
  verticalAlignable = 13,
}

// File-private alias, to avoid typing out the long version. Not a typealias because we need the value.
const Capability = VisualizationDataTableFilterCapability;

export class VisualizationDataTableFilterCapabilityManager {
  static capabilities: { [key in VisualizationDataTableFilterCapability]: VisualizationDataTableFilterType[] } = {
    [Capability.containerable]: [
      LayoutType.Grid,
      LayoutType.Stack,
    ],
    [Capability.labelable]: [
      ControlType.Dropdown,
      ControlType.SearchBar,
      ControlType.DateRange,
      ControlType.Toggle,
    ],
    [Capability.defaultable]: [
      ControlType.Toggle,
    ],
    [Capability.conditionable]: [
      ControlType.Toggle,
    ],
    [Capability.selectable]: [
      ControlType.Dropdown,
      ControlType.SearchBar,
      ControlType.DateRange,
      ControlType.Toggle,
      LayoutType.Grid,
      LayoutType.Stack,
    ],
    [Capability.dateModable]: [
      ControlType.DateRange,
    ],
    [Capability.dataPointReferencable]: [
      ControlType.DateRange,
      ControlType.Dropdown,
      ControlType.SearchBar,
    ],
    [Capability.multiDataPointReferencable]: [
      ControlType.SearchBar,
    ],
    [Capability.placeholderable]: [
      ControlType.Dropdown,
      ControlType.SearchBar,
    ],
    [Capability.searchTypable]: [
      ControlType.SearchBar,
    ],
    [Capability.helpTextable]: [
      LayoutType.Stack,
      LayoutType.Grid,
    ],
    [Capability.verticalAlignable]: [
      LayoutType.Grid,
    ],
    [Capability.columnLayoutable]: [
      LayoutType.Grid,
    ],
  };

  static can(type: VisualizationDataTableFilterType, capability: VisualizationDataTableFilterCapability): boolean {
    if (!(capability in this.capabilities)) {
      return false;
    }
    return this.capabilities[capability].includes(type);
  }

  static addDefaultProperties(m: VisualizationDataTableFilter): void {
    if (this.can(m.filter_type, Capability.labelable)) {
      const defaultLabel = m.is(ControlType.SearchBar) ? 'Search' : null;
      m.defineProperty('label_title', defaultLabel);
    }
    if (this.can(m.filter_type, Capability.defaultable)) {
      m.defineProperty('default_value', true);
    }
    if (this.can(m.filter_type, Capability.dateModable)) {
      m.defineProperty('date_filter_mode', DateFilterMode.FixedRange);
      m.defineProperty('rolling_range_unit', DurationUnit.day);
      m.defineProperty('rolling_range_amount', 1);
    }
    if (this.can(m.filter_type, Capability.dataPointReferencable)) {
      m.defineProperty('source_client_id', null);
      m.defineProperty('element_client_id', null);
      m.defineProperty('data_fusion_point_client_id', null);
      m.defineProperty('object_model_builtin_type', null);
      m.defineProperty('form_builtin_type', null);
    }
    if (this.can(m.filter_type, Capability.placeholderable)) {
      let placeholder: string;
      switch (m.filter_type) {
        case ControlType.Dropdown:
          placeholder = 'Select one...';
          break;
        case ControlType.SearchBar:
          placeholder = 'Search...';
          break;
        default:
          placeholder = '';
          break;
      }
      m.defineProperty('placeholder_text', placeholder);
    }
    if (this.can(m.filter_type, Capability.searchTypable)) {
      m.defineProperty('match_type', SearchMatchType.CloseVariant);
    }
    if (this.can(m.filter_type, Capability.helpTextable)) {
      m.defineProperty('help_text', '');
    }
    if (this.can(m.filter_type, Capability.columnLayoutable)) {
      m.defineProperty('layout_config', [1, 1]);
    }
    if (this.can(m.filter_type, Capability.verticalAlignable)) {
      m.defineProperty('vertical_alignment', VerticalAlignment.Bottom);
    }
  }
}